import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';
import { Header } from '../components';
import {
  ChartComponent, SeriesCollectionDirective, SeriesDirective,
  Inject as ChartInject,
  BarSeries, ColumnSeries, LineSeries, SplineAreaSeries,
  Category, Tooltip as ChartTooltip, DataLabel, Legend,
  AccumulationChartComponent, AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective, PieSeries,
  AccumulationTooltip, AccumulationDataLabel, AccumulationLegend,
} from '@syncfusion/ej2-react-charts';

const ORANGE = '#FF743A';
const BLUE   = '#256D9F';
const PIE_PALETTE = ['#FF743A', '#256D9F', '#f97316', '#60a5fa', '#fb923c', '#3b82f6'];

const formatCLP = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value);

const toDataSource = (categories, data) =>
  categories.map((cat, i) => ({ x: cat, y: data[i] ?? 0 }));

const KpiCard = ({ title, value, subtitle, color }) => (
  <div className="bg-white rounded-2xl p-5 flex-1 min-w-[180px] shadow-sm border border-gray-100">
    <p className="text-sm text-gray-500 mb-1">{title}</p>
    <p className="text-2xl font-bold" style={{ color }}>{value ?? '—'}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const clpTooltipRender = (args) => {
  if (args.point?.y !== undefined) {
    const formatted = Number(args.point.y).toLocaleString('es-CL');
    args.text = `${args.point.x} : $${formatted}`;
  }
};

const Dashboard = () => {
  const { currentColor } = useStateContext();
  const [meses, setMeses] = useState(1);
  const [kpis, setKpis] = useState({});
  const [ventasAnuales, setVentasAnuales] = useState(null);
  const [topVendedores, setTopVendedores] = useState(null);
  const [origenCotizaciones, setOrigenCotizaciones] = useState(null);
  const [stockLevels, setStockLevels] = useState(null);
  const [rentabilidad, setRentabilidad] = useState(null);
  const [metrosPorLamina, setMetrosPorLamina] = useState([]);

  const base = process.env.REACT_APP_BACKEND_URL;
  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [montoRes, tasaRes, clientesRes, inventarioRes,
          ventasRes, vendedoresRes, origenRes, stockRes, rentRes] =
          await Promise.all([
            axios.get(`${base}/api/dashboard/kpi/monto-aprobado-mes/`, { headers }),
            axios.get(`${base}/api/dashboard/kpi/tasa-conversion-mes/`, { headers }),
            axios.get(`${base}/api/dashboard/kpi/clientes-nuevos-mes/`, { headers }),
            axios.get(`${base}/api/dashboard/kpi/valor-inventario-total/`, { headers }),
            axios.get(`${base}/api/dashboard/chart/ventas-anuales/`, { headers }),
            axios.get(`${base}/api/dashboard/chart/top-vendedores/`, { headers }),
            axios.get(`${base}/api/dashboard/chart/origen-cotizaciones/`, { headers }),
            axios.get(`${base}/api/dashboard/chart/stock-levels/`, { headers }),
            axios.get(`${base}/api/dashboard/chart/rentabilidad/`, { headers }),
          ]);

        setKpis({
          montoAprobado: montoRes.data.value,
          tasaConversion: tasaRes.data.value,
          clientesNuevos: clientesRes.data.value,
          valorInventario: inventarioRes.data.value,
        });
        setVentasAnuales(ventasRes.data);
        setTopVendedores(vendedoresRes.data);
        setOrigenCotizaciones(origenRes.data);
        setStockLevels(stockRes.data);
        setRentabilidad(rentRes.data);
      } catch (err) {
        console.error('Error cargando dashboard:', err);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchMetros = async () => {
      try {
        const res = await axios.get(
          `${base}/api/dashboard/chart/metros-por-lamina/?meses=${meses}`,
          { headers }
        );
        setMetrosPorLamina(
          res.data.resultados.slice(0, 5).map(item => ({ x: item.lamina, y: item.metros_vendidos }))
        );
      } catch (err) {
        console.error('Error cargando metros por lámina:', err);
      }
    };
    fetchMetros();
  }, [meses]);

  const stockActualData = stockLevels
    ? toDataSource(stockLevels.categories, stockLevels.series[0].data).map((d, i) => ({
        ...d,
        fill: stockLevels.series[0].data[i] < 10 ? '#ef4444' : '#22c55e',
      }))
    : [];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="m-2 md:m-4 mt-4 p-2 md:p-4 bg-white rounded-3xl">
        <Header title="Dashboard" />

        {/* KPI Cards */}
        <div className="flex flex-wrap gap-4 mb-6">
          <KpiCard
            title="Monto aprobado del mes"
            value={kpis.montoAprobado != null ? formatCLP(kpis.montoAprobado) : null}
            color={ORANGE}
          />
          <KpiCard
            title="Tasa de conversión"
            value={kpis.tasaConversion != null ? `${kpis.tasaConversion}%` : null}
            subtitle="Cotizaciones → Órdenes"
            color={BLUE}
          />
          <KpiCard
            title="Clientes nuevos"
            value={kpis.clientesNuevos ?? null}
            subtitle="Primera orden aprobada este mes"
            color={BLUE}
          />
          <KpiCard
            title="Valor inventario"
            value={kpis.valorInventario != null ? formatCLP(kpis.valorInventario) : null}
            subtitle="Stock láminas (costo)"
            color={BLUE}
          />
        </div>

        {/* Fila 1: Ventas anuales + Origen cotizaciones */}
        <div className="flex flex-wrap lg:flex-nowrap gap-4 mb-4">
          <div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-2xl w-full lg:w-1/2 border border-gray-100">
            <p className="text-xl font-semibold mb-4">Ventas anuales</p>
            {ventasAnuales ? (
              <ChartComponent
                id="ventas-anuales"
                primaryXAxis={{ valueType: 'Category', labelRotation: -30, labelStyle: { size: '11px' } }}
                primaryYAxis={{ labelFormat: 'N0', title: 'CLP' }}
                tooltip={{ enable: true }}
                tooltipRender={clpTooltipRender}
                legendSettings={{ visible: true }}
                height="300px" width="100%"
              >
                <SeriesCollectionDirective>
                  {ventasAnuales.series.map((s, i) => (
                    <SeriesDirective
                      key={i}
                      dataSource={toDataSource(ventasAnuales.categories, s.data)}
                      xName="x" yName="y" type="Line" name={s.name}
                      fill={i === 0 ? ORANGE : BLUE}
                      width={2} marker={{ visible: true, width: 6, height: 6, fill: i === 0 ? ORANGE : BLUE }}
                    />
                  ))}
                </SeriesCollectionDirective>
                <ChartInject services={[LineSeries, Category, ChartTooltip, Legend]} />
              </ChartComponent>
            ) : <p className="text-gray-400 text-center mt-10">Cargando...</p>}
          </div>

          <div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-2xl w-full lg:w-1/2 border border-gray-100">
            <p className="text-xl font-semibold mb-4">Origen de cotizaciones</p>
            {origenCotizaciones && origenCotizaciones.labels.length > 0 ? (
              <AccumulationChartComponent
                id="origen-pie"
                tooltip={{ enable: true }}
                legendSettings={{ visible: true, position: 'Bottom' }}
                height="300px" width="100%"
                palettes={PIE_PALETTE}
              >
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    dataSource={origenCotizaciones.labels.map((label, i) => ({
                      x: label,
                      y: origenCotizaciones.series[i],
                    }))}
                    xName="x" yName="y" type="Pie"
                    innerRadius="40%"
                    dataLabel={{ visible: true, position: 'Outside', name: 'x' }}
                  />
                </AccumulationSeriesCollectionDirective>
                <ChartInject services={[PieSeries, AccumulationTooltip, AccumulationDataLabel, AccumulationLegend]} />
              </AccumulationChartComponent>
            ) : <p className="text-gray-400 text-center mt-10">Cargando...</p>}
          </div>
        </div>

        {/* Fila 2: Rentabilidad + Top 10 láminas */}
        <div className="flex flex-wrap lg:flex-nowrap gap-4 mb-4">
          <div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-2xl w-full lg:w-1/2 border border-gray-100">
            <p className="text-xl font-semibold mb-4">Rentabilidad</p>
            {rentabilidad ? (
              <ChartComponent
                id="rentabilidad"
                primaryXAxis={{ valueType: 'Category', labelRotation: -30, labelStyle: { size: '11px' } }}
                primaryYAxis={{ labelFormat: 'N0', title: 'CLP' }}
                tooltip={{ enable: true }}
                tooltipRender={clpTooltipRender}
                legendSettings={{ visible: true }}
                height="300px" width="100%"
              >
                <SeriesCollectionDirective>
                  {rentabilidad.series.map((s, i) => (
                    <SeriesDirective
                      key={i}
                      dataSource={toDataSource(rentabilidad.categories, s.data)}
                      xName="x" yName="y" type="SplineArea" name={s.name}
                      fill={i === 0 ? ORANGE : BLUE} opacity={0.5}
                    />
                  ))}
                </SeriesCollectionDirective>
                <ChartInject services={[SplineAreaSeries, Category, ChartTooltip, Legend]} />
              </ChartComponent>
            ) : <p className="text-gray-400 text-center mt-10">Cargando...</p>}
          </div>

          <div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-2xl w-full lg:w-1/2 border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <p className="text-xl font-semibold">Top 5 Láminas Vendidas</p>
              <select
                value={meses}
                onChange={(e) => setMeses(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              >
                <option value={1}>1 mes</option>
                <option value={3}>3 meses</option>
                <option value={12}>12 meses</option>
                <option value={24}>24 meses</option>
              </select>
            </div>
            {metrosPorLamina.length > 0 ? (
              <ChartComponent
                id="bar-laminas"
                primaryXAxis={{ valueType: 'Category', labelIntersectAction: 'Trim', maximumLabelWidth: 120, labelStyle: { size: '11px' } }}
                primaryYAxis={{ labelFormat: '{value} m²', title: 'Metros vendidos' }}
                tooltip={{ enable: true, format: '<b>${point.x}</b><br/>Metros: <b>${point.y} m²</b>' }}
                height="300px" width="100%" palettes={['#FF743A']}
              >
                <SeriesCollectionDirective>
                  <SeriesDirective
                    dataSource={metrosPorLamina}
                    xName="x" yName="y" type="Bar" name="Metros vendidos"
                    marker={{ dataLabel: { visible: true, position: 'Outer', font: { size: '11px' } } }}
                  />
                </SeriesCollectionDirective>
                <ChartInject services={[BarSeries, Category, ChartTooltip, DataLabel]} />
              </ChartComponent>
            ) : <p className="text-gray-400 text-center mt-10">Sin datos para el período</p>}
          </div>
        </div>

        {/* Fila 3: Stock levels + Top vendedores */}
        <div className="flex flex-wrap lg:flex-nowrap gap-4 mb-4">
          <div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-2xl w-full lg:w-1/2 border border-gray-100">
            <p className="text-xl font-semibold mb-1">Niveles de stock críticos</p>
            <p className="text-xs text-gray-400 mb-4">Rojo = bajo mínimo (10 m²)</p>
            {stockLevels && stockActualData.length > 0 ? (
              <ChartComponent
                id="stock-levels"
                primaryXAxis={{ valueType: 'Category', labelIntersectAction: 'Trim', maximumLabelWidth: 100, labelStyle: { size: '11px' } }}
                primaryYAxis={{ title: 'm²' }}
                tooltip={{ enable: true }}
                legendSettings={{ visible: true }}
                height="300px" width="100%"
              >
                <SeriesCollectionDirective>
                  <SeriesDirective
                    dataSource={stockActualData}
                    xName="x" yName="y" type="Column" name="Stock actual"
                    pointColorMapping="fill"
                    marker={{ dataLabel: { visible: true, position: 'Outer', font: { size: '11px' } } }}
                  />
                  <SeriesDirective
                    dataSource={toDataSource(stockLevels.categories, stockLevels.series[1].data)}
                    xName="x" yName="y" type="Column" name="Stock mínimo"
                    fill={BLUE} opacity={0.3}
                  />
                </SeriesCollectionDirective>
                <ChartInject services={[ColumnSeries, Category, ChartTooltip, Legend, DataLabel]} />
              </ChartComponent>
            ) : <p className="text-gray-400 text-center mt-10">Cargando...</p>}
          </div>

          <div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-2xl w-full lg:w-1/2 border border-gray-100">
            <p className="text-xl font-semibold mb-4">Top vendedores</p>
            {topVendedores && topVendedores.categories.length > 0 ? (
              <ChartComponent
                id="top-vendedores"
                primaryXAxis={{ valueType: 'Category', labelStyle: { size: '11px' } }}
                primaryYAxis={{ labelFormat: 'N0', title: 'Monto aprobado (CLP)' }}
                tooltip={{ enable: true }}
                tooltipRender={clpTooltipRender}
                height="300px" width="100%" palettes={['#FF743A']}
              >
                <SeriesCollectionDirective>
                  <SeriesDirective
                    dataSource={toDataSource(topVendedores.categories, topVendedores.series[0].data)}
                    xName="x" yName="y" type="Bar" name="Monto aprobado"
                    marker={{ dataLabel: { visible: true, position: 'Outer', font: { size: '11px' } } }}
                  />
                </SeriesCollectionDirective>
                <ChartInject services={[BarSeries, Category, ChartTooltip, DataLabel]} />
              </ChartComponent>
            ) : <p className="text-gray-400 text-center mt-10">Cargando...</p>}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
